/**
 * Il SETTE E MEZZO dal BFF di Limulo: le sei rotte `/v1/games/settemezzo/*` (`limulo-app/rotte.js` di QSetteMezzo), iniettate in `limuloApi` come `services/limuloScopa.ts` (stessa base, stessa
 * sessione, stesso refresh), e NON dentro `services/limuloApi.ts`: la zona del sette e mezzo resta fuori dal file di tutti. Le forme sono in `lib/gamehost/rest.ts` e `lib/gamehost/ticket.ts`
 * (il trasporto comune, ADR-0235 §4), qui se ne passa il dominio; i consumatori sono la Hub (`components/settemezzo/*`) e l'hook della partita (`hooks/useSettemezzoMatch.ts`).
 *
 * 🔴 Le POST non si ritentano da sole (`limuloBaseQuery` non ritenta nessuna POST) e non si mettono in cache; le letture non restano in cache oltre la schermata (`keepUnusedDataFor: 0`):
 * un tavolo finito non deve ricomparire dalla cache.
 *
 * `/*#__PURE__*\/` e NIENTE destrutturazione degli hook: i consumatori usano `settemezzoApi.useXxx()` dentro il loro corpo. Cosi, se nessun consumatore sopravvive alla build (la produzione:
 * il flag piega via la Hub e le rotte), il bundler toglie anche questo modulo, e con lui le definizioni delle rotte.
 */
import { limuloApi } from '@/services/limuloApi';
import { corpoDi, entraBody, ingressoOf, mescolataOf, restDi, rivincitaBody, tavoliMieiOf, tavoloCreatoOf } from '@/lib/gamehost/rest';
import type { TavoliMiei, TavoloCreato } from '@/lib/gamehost/rest';
import { ticketBody, ticketGrantOf, ticketRoute } from '@/lib/gamehost/ticket';
import type { TicketGrant } from '@/lib/gamehost/ticket';
import { SETTEMEZZO_DOMINIO, POSTI_AMMESSI, creaTavoloSettemezzoBody } from '@/lib/settemezzo/gioco';
import type { RichiestaTavolo } from '@/lib/settemezzo/gioco';

const REST = restDi(SETTEMEZZO_DOMINIO);
const TICKET_ROUTE = ticketRoute(SETTEMEZZO_DOMINIO);

export const settemezzoApi = /*#__PURE__*/ limuloApi.injectEndpoints({
  endpoints: (builder) => ({
    /** `settemezzo.table.mine.list` — i tavoli VIVI di chi chiama («I miei tavoli / Riprendi»). */
    getMySettemezzoTables: builder.query<TavoliMiei, void>({
      query: () => ({ url: REST.tavoliMiei }),
      transformResponse: (response: unknown) => tavoliMieiOf(response, POSTI_AMMESSI),
      keepUnusedDataFor: 0,
    }),
    /**
     * `settemezzo.table.create` — «Gioca» (casual), «Invita un amico» (privato) e la partita contro il robot (casual + `livello`): sempre 2 posti.
     * ⚠️ Il campo `robot: { livello }` arriva al game host solo dopo il mirror di app-qsettemezzo su Limulo staging (INNESTO §9 di QSetteMezzo): finche il BFF lo scarta, il tavolo nasce senza robot.
     */
    createSettemezzoTable: builder.mutation<TavoloCreato | null, RichiestaTavolo>({
      query: (a) => ({ url: REST.tavoli, method: 'POST', body: creaTavoloSettemezzoBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `settemezzo.table.join` — «Entra con un codice»: il posto lo sceglie il game host. */
    joinSettemezzoTable: builder.mutation<{ tableId: string } | null, { tableId: string; invito: string; idempotencyKey: string }>({
      query: (a) => ({ url: REST.posti(a.tableId), method: 'POST', body: entraBody(a) }),
      transformResponse: (response: unknown, _meta, a) => ingressoOf(response, a.tableId),
    }),
    /** `settemezzo.table.rematch` — la rivincita di un tavolo FINITO: un tavolo nuovo (201) o quello gia aperto da un altro (200). */
    rematchSettemezzoTable: builder.mutation<TavoloCreato | null, { tableId: string; idempotencyKey: string }>({
      query: (a) => ({ url: REST.rivincita(a.tableId), method: 'POST', body: rivincitaBody(a) }),
      transformResponse: (response: unknown) => tavoloCreatoOf(response),
    }),
    /** `settemezzo.table.mescolata.read` — il record di una smazzata finita (`qlash-fair-v1`). La verifica a schermo non e in questa PR: si legge e basta. */
    getSettemezzoMescolata: builder.query<Record<string, unknown> | null, { tableId: string; smazzata: number }>({
      query: (a) => ({ url: REST.mescolata(a.tableId, a.smazzata) }),
      transformResponse: (response: unknown) => mescolataOf(response),
      keepUnusedDataFor: 0,
    }),
    /** `settemezzo.session.ticket.issue` — il ticket MONOUSO (30 s) del socket del tavolo: corpo `{ tableId }` (lo scopo lo compone la rotta del kit). Chi lo chiede lo consuma subito. */
    issueSettemezzoTicket: builder.mutation<TicketGrant | null, { tableId: string }>({
      query: ({ tableId }) => ({ url: TICKET_ROUTE, method: 'POST', body: ticketBody(tableId) }),
      transformResponse: (response: unknown) => ticketGrantOf(corpoDi(response)),
    }),
  }),
});
